import React from 'react';
import ReactDOM from 'react-dom';
import Headlines, { usableStories, DEFAULT_STORIES_PER_ROUND } from '@dazn/headlines';
import _get from 'lodash/get';
import UPshowState from '../UPshowState';
import StateFactory from '../StateFactory';
import { StatePreconditionError } from '../Errors';
import UPshowNowService from '../../services/UPshowNowService';
import MediaService from '../../services/MediaService';
import ScreenService from '../../services/ScreenService';
import HwFeatureService from '../../services/HwFeatureService';
import SettingsService from '../../services/SettingsService';
import UpshowLogger from '../../Logger';
import { isLowEndDevice } from '../../utils/models';

// The run ends by itself (onDone, ~143 s); the playlist time is not a stop condition and the watchdog is the only
// safety net. Longer than the longest scene (QR, 20 s) plus margin for slow device timers.
const WATCHDOG_MS = 30000;
const TAKEOVER_CHECK_MS = 10000;

const WATCHDOG_TIMER = 'headlines_watchdog';

// Player event -> [UpshowLogger level, metricData counter]
const EVENTS = {
    image: ['warn', 'image_failures'],
    item_fallback: ['warn', 'item_fallbacks'],
    ready_timeout: ['warn', 'ready_timeouts'],
    scene_error: ['error', 'scene_errors'],
    skipped_items: ['warn', 'skipped_items'],
    watchdog: ['error', 'watchdog'],
    crash: ['error', 'crash']
};

class HeadlinesState extends UPshowState {
    get name () {
        return 'upshownow';
    }

    async preload () {
        const channelId = this.state.state.itemId;
        if (!channelId) {
            throw new StatePreconditionError('No Headlines channel provided');
        }

        const channel = await UPshowNowService.getChannel(channelId);
        if (!channel) {
            throw new StatePreconditionError('No Headlines channel found');
        }

        this.logged = new Set();
        this.metricData = { ...this.metricData, id: channelId };

        const content = await UPshowNowService.getChannelContent(channel);
        const stories = usableStories(content);
        if (stories.length < content.length) {
            this._onEvent('skipped_items', { count: content.length - stories.length });
        }
        if (stories.length === 0) {
            throw new StatePreconditionError('No usable stories for Headlines');
        }

        const organizationId = SettingsService.getSetting('organization_id');
        // The landing lists the stories whose ids come in content_ids: only the ones of the round the screen plays.
        // Without any linkable story there is no QR scene (the landing would be empty).
        const contentIds = stories.slice(0, DEFAULT_STORIES_PER_ROUND).filter(story => story.feedUrl).map(story => story.id);
        this.qrUrl = contentIds.length
            ? `${process.env.REACT_APP_HEADLINES_LANDING_URL}?id=${channelId}&org=${organizationId}&content_ids=${JSON.stringify(contentIds)}`
            : undefined;
        this.channel = { id: channel.id, title: channel.title, thumbnail: channel.thumbnail, content: stories };

        this._render(false);

        return this.state.readyPromise;
    }

    play () {
        this.state.playing = true;
        this.metricData.start = new Date();

        // -1 keeps the impression interval but skips the duration timer, as takeovers do.
        this.state.duration = -1;
        this._playDuration();
        this._armWatchdog();

        if (_get(this.state, 'state.takeoverData.behavior') === 'takeover') {
            this.setInterval('checkschedule', () => {
                const takeOverSchedule = MediaService.getActiveItemsByBehavior('takeover', ['upshownow'])[0];
                if (_get(takeOverSchedule, 'channel.id') !== _get(this.state, 'state.takeoverData.channel.id')) {
                    this.raiseDone('Schedule has finished');
                }
            }, TAKEOVER_CHECK_MS);
        }

        this._render(true);

        return this.state.playPromise;
    }

    stop () {
        this.stopped = true;
        this.clearTimeout(WATCHDOG_TIMER);
        this.clearInterval('checkschedule');
        this._render(false);

        return super.stop();
    }

    destroy (currentStateName) {
        this.stopped = true;

        return super.destroy(currentStateName);
    }

    _render (playing) {
        ReactDOM.render(<Headlines
            channel={this.channel}
            playing={playing}
            qrUrl={this.qrUrl}
            motion={isLowEndDevice() ? 'none' : 'full'}
            onReady={this._guard(() => this.raiseReady())}
            onPlaying={this._guard(() => this.raisePlaying())}
            onProgress={this._guard(() => this._armWatchdog())}
            onDone={this._guard(reason => this._onDone(reason))}
            onError={this._guard(error => this._onError(error))}
            onEvent={this._guard((type, data) => this._onEvent(type, data))}
        />, this.node);
    }

    // A stopped (or destroyed) instance must stay silent even if the player calls back late.
    _guard (fn) {
        return (...args) => {
            if (!this.stopped) fn(...args);
        };
    }

    _armWatchdog () {
        this.clearTimeout(WATCHDOG_TIMER);
        this.setTimeout(WATCHDOG_TIMER, () => {
            this._onEvent('watchdog', { timeout_ms: WATCHDOG_MS });
            this.raiseError(new Error('Headlines watchdog timeout'));
        }, WATCHDOG_MS);
    }

    // The next state preloads before this one is stopped, so the watchdog must not outlive the run.
    _onDone (reason) {
        this.clearTimeout(WATCHDOG_TIMER);
        if (reason === 'no-content') {
            this.raiseSkip('Headlines has no content');
        } else {
            this.raiseDone(reason);
        }
    }

    _onError (error) {
        this.clearTimeout(WATCHDOG_TIMER);
        this._onEvent('crash', { reason: _get(error, 'reason'), error: _get(error, 'message') });
        this.raiseError(error);
    }

    // UpshowLogger has no dedupe: log once per (type, item), count every occurrence in the swapstate metric.
    _onEvent (type, data = {}) {
        const [level, counter] = EVENTS[type] || ['debug', type];
        this.metricData[counter] = (this.metricData[counter] || 0) + 1;

        const key = `${type}:${data.item_id !== undefined ? data.item_id : data.scene}`;
        if (this.logged.has(key)) return;
        this.logged.add(key);

        UpshowLogger[level](['headlines', type], `Headlines ${type}`, {
            ...data,
            channel_id: this.metricData.id,
            device_id: SettingsService.getSetting('device_id'),
            organization_id: SettingsService.getSetting('organization_id')
        });
    }

    static appliesTo () {
        // Beta orgs only: everyone else keeps the legacy state (priority 10) until Headlines is rolled out.
        // `headlines` is its own hardware feature: Xumo plays Headlines but never the legacy UPshowNow.
        return SettingsService.hasFeature('beta') && !ScreenService.isVertical && HwFeatureService.isSupported('headlines');
    }
}

StateFactory.register('upshownow', HeadlinesState, 1);

export default HeadlinesState;
